import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { NavVisCard } from '../../../components/UploadCard/NavVisCard';
import { NavVisLoginModal } from '../../../modules/navvis/NavVisLoginModal';
import { NavVisProjectSelectModal } from '../../../modules/navvis/NavVisProjectSelectModal';
import { useT } from '../../../translation/src';
import { NavVisAuthResult, NavVisDataset, useNavvisImportDatasetMutation, useProjectsQuery } from '../../../types/graphqlTypes';

export const useNavVis = () => {
  const { organisationId = '' } = useParams<{ organisationId: string }>();
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [selectModalOpen, setSelectModalOpen] = useState(false);
  const [authResult, setAuthResult] = useState<NavVisAuthResult | null>(null);
  const [baseUrl, setBaseUrl] = useState<string | undefined>(undefined);

  const [importDataset] = useNavvisImportDatasetMutation();
  const projectsQuery = useProjectsQuery({ variables: { organisationId } });

  const importStarted = useT('NavVis import started');
  const importFailed = useT('NavVis import failed');

  const openLoginModal = useCallback(() => {
    setLoginModalOpen(true);
  }, []);

  const closeLoginModal = useCallback(() => {
    setLoginModalOpen(false);
  }, []);

  const closeSelectModal = useCallback(() => {
    setSelectModalOpen(false);
    setAuthResult(null);
    setBaseUrl(undefined);
  }, []);

  const handleAuthSuccess = useCallback((result: NavVisAuthResult, url?: string) => {
    setAuthResult(result);
    setBaseUrl(url);
    setLoginModalOpen(false);
    setSelectModalOpen(true);
  }, []);

  const handleSelectDataset = useCallback(
    async (dataset: NavVisDataset, projectName: string, projectDescription: string) => {
      if (!authResult?.accessToken) return;

      try {
        await importDataset({
          variables: {
            accessToken: authResult.accessToken,
            siteId: dataset.siteId,
            siteName: dataset.siteName,
            datasetName: dataset.name,
            referenceSystemId: dataset.referenceSystemId,
            organisationId,
            projectName,
            projectDescription: projectDescription || undefined,
            baseUrl: baseUrl || undefined,
          },
        });

        toast.success(importStarted);
        projectsQuery.refetch();
        closeSelectModal();
      } catch (error) {
        const message = error instanceof Error ? error.message : importFailed;
        toast.error(message);
      }
    },
    [authResult, organisationId, baseUrl, importDataset, importStarted, importFailed, projectsQuery, closeSelectModal],
  );

  const navVisCard = useMemo(() => <NavVisCard onClick={openLoginModal} />, [openLoginModal]);

  const modals = useMemo(
    () => (
      <>
        <NavVisLoginModal open={loginModalOpen} onClose={closeLoginModal} onSuccess={handleAuthSuccess} />
        <NavVisProjectSelectModal open={selectModalOpen} onClose={closeSelectModal} authResult={authResult} baseUrl={baseUrl} onSelectDataset={handleSelectDataset} />
      </>
    ),
    [loginModalOpen, closeLoginModal, handleAuthSuccess, selectModalOpen, closeSelectModal, authResult, baseUrl, handleSelectDataset],
  );

  return {
    navVisCard,
    modals,
    openLoginModal,
  };
};
